//#region src/first-party-apps.ts
/**
* The list. One row per first-party app; a new row is a code change and a review.
*
* `admin-app` is the organization admin UI, extracted from the DP shell. Its mount is `/admin`, the
* URL the built-in admin pages had, and `owner` and `admin` are the org roles the shell's
* `canAccessAdmin` admitted before the extraction.
*
* `default-shell` is the platform's default root shell, built in `witness-ai/zion-shell-internal`
* (layered shell surfaces, D24, R-20 Q5, R-28). Every principal needs a root shell, so all three
* org roles see it, and every resident org is entitled to it. Its root surface declares no
* `mountPath`; the root-shell assignment picks the prefix (D22). `/default-shell` is the one path
* the CP admits for a legacy `shell[]` row of the app, and the first segment it reserves keeps
* third-party apps off the shell's own name. Root eligibility is a separate list,
* `ROOT_SHELL_APPS`, which also names `sdk-smoke` (R-17); that app is not first-party.
*/
const FIRST_PARTY_APPS = Object.freeze([Object.freeze({
	appId: "admin-app",
	mountPath: "/admin",
	visibleToOrgRoles: Object.freeze(["owner", "admin"])
}), Object.freeze({
	appId: "default-shell",
	mountPath: "/default-shell",
	visibleToOrgRoles: Object.freeze([
		"owner",
		"admin",
		"member"
	])
})]);
/** The row for `appId`, or `undefined` for an app the list does not name. */
function firstPartyApp(appId) {
	return FIRST_PARTY_APPS.find((row) => row.appId === appId);
}
/** True when the list names `appId`. */
function isFirstPartyApp(appId) {
	return firstPartyApp(appId) !== void 0;
}
/**
* The first segment of every first-party mount path, sorted and deduplicated:
* `['admin', 'default-shell']`.
*
* `mount-path.ts` folds these into {@link RESERVED_MOUNT_SEGMENTS}, so a third-party app cannot
* mount under a first-party app's prefix; the reserved list holds 18 names.
*/
const RESERVED_FIRST_PARTY_SEGMENTS = Object.freeze([...new Set(FIRST_PARTY_APPS.map((row) => row.mountPath.slice(1).split("/", 1).join("")))].sort());
//#endregion
export { FIRST_PARTY_APPS, RESERVED_FIRST_PARTY_SEGMENTS, firstPartyApp, isFirstPartyApp };

//# sourceMappingURL=first-party-apps.js.map